import { Loc } from 'main.core';
import { mapState, mapWritableState, mapActions } from 'ui.vue3.pinia';

import { resetCaptcha, CaptchaAnalytics, CaptchaScenarios } from 'b24network.vue.adaptivecaptcha';
import {
	LegalLinks,
	PasswordEditForm,
	PasswordRestoreForm,
	PhoneAndEmailConfirmationForm,
} from 'b24network.vue.base-components';
import {
	UserRedirect,
	LoginValidator,
	SharedApi,
} from 'b24network.vue.cross-scenario-shared-js';

import { Ajax } from '../../api/ajax';
import { Analytics } from '../../api/analytics';
import { useLoggingInStore } from '../../pinia/store';
import {
	requestPasswordRestoreData,
	isPasswordRestoreCaptchaPrefetchError,
} from '../../services/password-restore-service';

import '../../../css/components/pages/password-restore.css';

export const PasswordRestore = {
	name: 'PasswordRestore',

	components: {
		LegalLinks,
		PasswordEditForm,
		PasswordRestoreForm,
		PhoneAndEmailConfirmationForm,
	},

	data(): Object
	{
		return {
			password: '',
			emailCode: '',
			phoneCode: '',
			loginError: '',
			captchaError: '',
			emailCodeError: '',
			passwordError: '',
			resendTimeout: 60,
			userAccount: null,
			phoneCodeError: '',
			isPasswordOk: false,
			captchaResult: null,
			phoneCodeIsOk: false,
			emailCodeIsOk: false,
			smsResendDataSignature: '',
			passwordRestoreResult: null,
			isProcessingEmailCode: false,
			isProcessingPhoneCode: false,
			isEmailCodeManualOnly: false,
			isPhoneCodeManualOnly: false,
			emailVerificationCycle: 0,
			phoneVerificationCycle: 0,
			isProcessingPasswordEnter: false,
			// При выключенной капче не показываем остров до конца silent-restore, иначе один кадр с формой «Восстановление пароля»
			isPasswordRestoreBodyVisible: false,
			isPrefetchCaptchaFailure: false,
			isLoading: true,
			rawAccounts: [],
		};
	},

	computed: {
		...mapWritableState(useLoggingInStore, [
			'login',
			'typeOfCaptchaOnPage',
			'navigationButtonType',
		]),

		...mapState(useLoggingInStore, [
			'legalLinks',
			'userCheckWord',
			'defaultCountry',
			'captchaSettings',
			'passwordRestorePrefetch',
			'passwordRestorePrefetchCaptchaFailure',
			'redirectUrlWhenAuthorized',
		]),

		passwordRestoreLabel(): string
		{
			return this.isEmailLogin()
				? Loc.getMessage('EXT_B24NET_LOGGING_IN_PASS_RESTORE_EMAIL')
				: Loc.getMessage('EXT_B24NET_LOGGING_IN_PASS_RESTORE_PHONE')
			;
		},

		isPasswordRestoreLoginStep(): boolean
		{
			return !this.isPasswordRestoreMailOrSmsSent;
		},

		isPasswordRestoreMailOrSmsSent(): boolean
		{
			return this.passwordRestoreResult !== null;
		},

		isEmailConfirmationCodeStep(): boolean
		{
			return this.isEmailLogin()
				&& this.isPasswordRestoreMailOrSmsSent
				&& !this.emailCodeIsOk
			;
		},

		isPhoneConfirmationCodeStep(): boolean
		{
			return this.isPasswordRestoreMailOrSmsSent
				&& LoginValidator.isPhoneLogin(this.login)
				&& !this.phoneCodeIsOk
			;
		},

		isPasswordEditStep(): boolean
		{
			return this.userAccount
				&& (this.phoneCodeIsOk || this.emailCodeIsOk)
			;
		},

		isPageTitleShown(): boolean
		{
			return (!this.isPasswordRestoreMailOrSmsSent && !this.isPrefetchCaptchaFailure)
				|| !this.isEmailLogin()
			;
		},

		isConfirmationResendCaptchaShown(): boolean
		{
			return this.captchaSettings.isEnabled
				&& this.resendTimeout <= 0
				&& this.isPasswordRestoreMailOrSmsSent
			;
		},

		isResendLinkDisabled(): boolean
		{
			if (!this.isConfirmationResendCaptchaShown)
			{
				return false;
			}

			return !this.captchaResult;
		},

		showEmailCodeCaptcha(): boolean
		{
			return this.isEmailConfirmationCodeStep && this.isConfirmationResendCaptchaShown;
		},

		showPhoneCodeCaptcha(): boolean
		{
			return this.isPhoneConfirmationCodeStep && this.isConfirmationResendCaptchaShown;
		},

		showEmailCodeContinueButton(): boolean
		{
			return this.showEmailCodeCaptcha || this.isEmailCodeManualOnly;
		},

		showPhoneCodeContinueButton(): boolean
		{
			return this.showPhoneCodeCaptcha || this.isPhoneCodeManualOnly;
		},

		isEmailCodeContinueDisabled(): boolean
		{
			return this.isProcessingEmailCode
				|| (this.showEmailCodeCaptcha && !this.captchaResult)
			;
		},

		isPhoneCodeContinueDisabled(): boolean
		{
			return this.isProcessingPhoneCode
				|| (this.showPhoneCodeCaptcha && !this.captchaResult)
			;
		},

		headingText(): string
		{
			if (this.isEmailConfirmationCodeStep && !this.isPrefetchCaptchaFailure)
			{
				return Loc.getMessage('EXT_B24NET_LOGGING_IN_CHECK_EMAIL');
			}

			if (
				this.isPageTitleShown
				|| (this.isPrefetchCaptchaFailure && this.isEmailLogin())
				|| this.isPasswordEditStep
			)
			{
				return Loc.getMessage('EXT_B24NET_LOGGING_IN_PASSWORD_RESTORE');
			}

			return '';
		},
	},

	async created(): void
	{
		if (
			this.passwordRestorePrefetchCaptchaFailure?.login === this.login
		)
		{
			this.applyPrefetchCaptchaFailure(this.passwordRestorePrefetchCaptchaFailure);
			this.clearPasswordRestorePrefetchCaptchaFailure();
			this.isLoading = false;
			await this.$nextTick();
			this.syncDocumentTitle();

			return;
		}

		const isCaptchaEnabled = this.captchaSettings.isEnabled;

		if (isCaptchaEnabled)
		{
			this.isPasswordRestoreBodyVisible = true;
		}

		try
		{
			if (
				this.passwordRestorePrefetch
				&& this.passwordRestorePrefetch.login === this.login
			)
			{
				this.applyPasswordRestorePrefetch(this.passwordRestorePrefetch);
				this.clearPasswordRestorePrefetch();
				this.isLoading = false;

				return;
			}

			await this.tryRestorePasswordForAccount();
			Analytics.emitSendEmailRestorePasswordEvent(this.userAccount?.id ?? null);
		}
		catch (errorResponse)
		{
			this.updatePasswordErrorIfAny(errorResponse);
		}
		finally
		{
			if (!isCaptchaEnabled)
			{
				this.isPasswordRestoreBodyVisible = true;
			}

			await this.$nextTick();
			this.syncDocumentTitle();
		}
	},

	mounted(): void
	{
		this.typeOfCaptchaOnPage = this.captchaSettings.captchaType;

		if (this.isPasswordRestoreBodyVisible)
		{
			this.syncDocumentTitle();
		}

		this.navigationButtonType = 'enter';
	},

	watch: {
		isPasswordRestoreLoginStep: {
			handler(isShown, wasShown): void
			{
				if (isShown)
				{
					this.typeOfCaptchaOnPage = this.captchaSettings.captchaType;

					if (wasShown === false)
					{
						Analytics.emitShowForgotPasswordRestorePageEvent(this.userAccount?.id ?? null);
					}
				}
			},
		},

		headingText(): void
		{
			if (this.isPasswordRestoreBodyVisible)
			{
				this.syncDocumentTitle();
			}
		},

		isEmailConfirmationCodeStep(isShown): void
		{
			if (isShown)
			{
				this.typeOfCaptchaOnPage = this.captchaSettings.captchaType;
				Analytics.emitPasswordRestoreEmailSentPageShown();
			}
		},

		isPhoneConfirmationCodeStep(isShown): void
		{
			if (isShown)
			{
				Analytics.emitShowEnterSmsCodePageEvent();
				this.typeOfCaptchaOnPage = this.captchaSettings.captchaType;
			}
		},

		isPasswordEditStep(isShown): void
		{
			if (isShown)
			{
				this.typeOfCaptchaOnPage = '';
				Analytics.emitShowSetNewPasswordPageEvent();
			}
		},
	},

	methods: {
		...mapActions(useLoggingInStore, [
			'goToLoggingInSubScenario',
			'clearPasswordRestorePrefetch',
			'clearPasswordRestorePrefetchCaptchaFailure',
		]),

		syncDocumentTitle(): void
		{
			if (this.headingText)
			{
				document.title = this.headingText;

				return;
			}

			document.title = Loc.getMessage('EXT_B24NET_LOGGING_IN_PASSWORD_RESTORE');
		},

		applyPasswordRestorePrefetch(prefetch: Object): void
		{
			this.isPrefetchCaptchaFailure = false;
			this.userAccount = prefetch.account;
			this.login = prefetch.login;
			this.passwordRestoreResult = prefetch.restoreResult;
			this.resendTimeout = prefetch.resendTimeout;
			this.captchaResult = null;
			this.resetVerificationCycles();
		},

		applyPrefetchCaptchaFailure(failure: Object): void
		{
			this.userAccount = failure.account;
			this.login = failure.login;
			this.captchaError = '';
			this.resendTimeout = 0;
			this.isPrefetchCaptchaFailure = true;
			this.isPasswordRestoreBodyVisible = true;
			this.passwordRestoreResult = null;
			this.captchaResult = null;
			this.resetVerificationCycles();
		},

		isEmailLogin(): boolean
		{
			// email OR social
			return !LoginValidator.isPhoneLogin(this.login);
		},

		onLoginChange(newLogin): void
		{
			this.resetVerificationCycles();
			this.loginError = '';
			this.login = newLogin;
		},

		async onPassRestoreFormContinueClick(): void
		{
			try
			{
				await this.loadUserAccounts();

				if (this.isEmailLogin() && this.rawAccounts.length > 0 && !this.userAccount)
				{
					return;
				}

				await this.tryRestorePasswordForAccount();
				// For login-phone back-end returns normalized phone number to E164 format
				this.login = this.userAccount.login;
				Analytics.emitSendEmailRestorePasswordEvent(this.userAccount.id ?? null);
			}
			catch (errorResponse)
			{
				this.updateCaptchaErrorIfAny(errorResponse);
				this.updateLoginErrorIfAny(errorResponse);
			}

			resetCaptcha(this.captchaSettings.captchaType);
		},

		async loadUserAccounts(): void
		{
			const accountResponse = await SharedApi.loadAccountsByLogin(
				this.login,
				this.defaultCountry,
			);

			if (accountResponse.data?.length === 0)
			{
				this.rawAccounts = [];
				this.loginError = this.isEmailLogin()
					? Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_EMAIL')
					: Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_PHONE')
				;
			}
			else
			{
				this.rawAccounts = accountResponse.data;

				if (accountResponse.data?.length === 1)
				{
					this.userAccount = this.extractAccountData(accountResponse.data[0]);
				}
				else
				{
					this.userAccount = this.extractAccountData(this.findRawAccountForCurrentLogin());
				}

				if (!this.userAccount)
				{
					this.loginError = this.isEmailLogin()
						? Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_EMAIL')
						: Loc.getMessage('EXT_B24NET_LOGGING_IN_NOT_FOUND_PHONE')
					;
				}
			}
		},

		findRawAccountForCurrentLogin(): Object
		{
			return this.rawAccounts.find((account) => (account.LOGIN === this.login));
		},

		extractAccountData(rawAccountData: ?Object): Object
		{
			if (!rawAccountData?.LOGIN || !rawAccountData?.ID)
			{
				return null;
			}

			return {
				id: rawAccountData.ID,
				email: rawAccountData.EMAIL,
				login: rawAccountData.LOGIN,
				avatarUrl: rawAccountData.PERSONAL_PHOTO ?? '',
				fullName: rawAccountData.NAME
					? `${rawAccountData.NAME} ${rawAccountData.LAST_NAME}`
					: '',
			};
		},

		async tryRestorePasswordForAccount(): void
		{
			const restoreData = await requestPasswordRestoreData({
				login: this.login,
				defaultCountry: this.defaultCountry,
				captchaResult: this.captchaResult,
			});
			this.userAccount = restoreData.account;
			this.login = restoreData.login;
			this.passwordRestoreResult = restoreData.restoreResult;
			this.resendTimeout = restoreData.resendTimeout;
			this.isPrefetchCaptchaFailure = false;
			this.captchaResult = null;
			this.resetVerificationCycles();
		},

		updatePasswordErrorIfAny(errorResponse): void
		{
			if (errorResponse instanceof Error)
			{
				this.passwordError = errorResponse.message ?? Loc.getMessage('EXT_B24NET_LOGGING_IN_ERROR');

				return;
			}

			if (!errorResponse.errors?.length)
			{
				return;
			}

			const expired = errorResponse.errors
				.find((err) => err.code === 'CHECKWORD_EXPIRED')
				?.message;

			if (expired)
			{
				location.reload();

				return;
			}

			const passwordError = (errorResponse.errors ?? [])
				.find((err) => err.code !== 'ERROR_CAPTCHA')
				?.message ?? ''
			;

			if (passwordError)
			{
				// For some historical reason backend appends <br> to error messages
				this.passwordError = passwordError.replace('<br>', '') ?? '';
			}
		},

		updateLoginErrorIfAny(errorResponse): void
		{
			if (!errorResponse.errors?.length)
			{
				return;
			}

			const loginError = (errorResponse.errors ?? [])
				.find((err) => err.code !== 'ERROR_CAPTCHA')
			;

			if (loginError)
			{
				if (loginError.code === 'REQUEST_WRONG')
				{
					this.loginError = Loc.getMessage(
						'EXT_B24NET_LOGGING_IN_UNKNOWN_ERROR_PASS_RESTORE',
					);
				}
				else
				{
					// For some historical reason backend appends <br> to error messages
					this.loginError = loginError.message.replace('<br>', '') ?? '';
				}
			}
		},

		updateCaptchaErrorIfAny(errorResponse): void
		{
			if (isPasswordRestoreCaptchaPrefetchError(errorResponse))
			{
				const captchaError = (errorResponse.errorResponse?.errors ?? [])
					.find((err) => err.code === 'ERROR_CAPTCHA')
					?.message ?? ''
				;

				if (captchaError)
				{
					this.captchaError = captchaError;
				}

				return;
			}

			if (errorResponse instanceof Error)
			{
				this.captchaError = '';

				return;
			}

			if (!errorResponse.errors?.length)
			{
				return;
			}

			const captchaError = (errorResponse.errors ?? [])
				.find((err) => err.code === 'ERROR_CAPTCHA')
				?.message ?? ''
			;

			if (captchaError)
			{
				this.captchaError = captchaError;
			}
		},

		onPhoneCodeChange(newCode): void
		{
			this.phoneCode = newCode;
			this.phoneCodeError = '';
		},

		onEmailCodeChange(newCode): void
		{
			this.emailCode = newCode;
			this.emailCodeError = '';
		},

		onEmailCodeReady(code: string): void
		{
			if (this.isEmailCodeManualOnly || this.showEmailCodeCaptcha)
			{
				return;
			}

			this.submitEmailCode(code);
		},

		onPhoneCodeReady(code: string): void
		{
			if (this.isPhoneCodeManualOnly || this.showPhoneCodeCaptcha)
			{
				return;
			}

			this.submitPhoneCode(code);
		},

		onEmailCodeContinueClick(): void
		{
			if (this.isEmailCodeContinueDisabled)
			{
				return;
			}

			this.submitEmailCode(this.emailCode);
		},

		onPhoneCodeContinueClick(): void
		{
			if (this.isPhoneCodeContinueDisabled)
			{
				return;
			}

			this.submitPhoneCode(this.phoneCode);
		},

		async submitEmailCode(code: string): Promise<void>
		{
			if (!this.passwordRestoreResult?.USER_SIGN)
			{
				this.isEmailCodeManualOnly = true;
				this.emailCodeError = this.passwordError || Loc.getMessage('EXT_B24NET_LOGGING_IN_ERROR');
				await this.$nextTick();
				this.$refs.emailConfirmationForm?.focus();

				return;
			}

			if (this.isProcessingEmailCode || code === '')
			{
				return;
			}

			this.isProcessingEmailCode = true;
			const verificationCycle = this.emailVerificationCycle;
			this.emailCodeError = '';
			this.emailCodeIsOk = false;

			try
			{
				await Ajax.verifyPasswordRestoreEmailCode(
					code,
					this.passwordRestoreResult.USER_SIGN,
				);
				if (verificationCycle !== this.emailVerificationCycle)
				{
					return;
				}

				this.passwordError = '';
				this.emailCodeIsOk = true;
			}
			catch (errorResponse)
			{
				if (verificationCycle !== this.emailVerificationCycle)
				{
					return;
				}

				this.isEmailCodeManualOnly = true;
				this.emailCodeError = this.getVerificationCodeErrorMessage(errorResponse);
				await this.$nextTick();
				this.$refs.emailConfirmationForm?.focus();
			}
			finally
			{
				if (verificationCycle === this.emailVerificationCycle)
				{
					this.isProcessingEmailCode = false;
				}
			}
		},

		async submitPhoneCode(code: string): Promise<void>
		{
			if (!this.passwordRestoreResult?.USER_SIGN)
			{
				this.isPhoneCodeManualOnly = true;
				this.phoneCodeError = this.passwordError || Loc.getMessage('EXT_B24NET_LOGGING_IN_ERROR');
				await this.$nextTick();
				this.$refs.phoneConfirmationForm?.focus();

				return;
			}

			if (this.isProcessingPhoneCode || code === '')
			{
				return;
			}

			this.isProcessingPhoneCode = true;
			const verificationCycle = this.phoneVerificationCycle;
			this.phoneCodeError = '';
			this.phoneCodeIsOk = false;

			try
			{
				await Ajax.verifyPasswordRestorePhoneCode(
					code,
					this.passwordRestoreResult.USER_SIGN,
				);
				if (verificationCycle !== this.phoneVerificationCycle)
				{
					return;
				}

				this.phoneCodeIsOk = true;
			}
			catch (errorResponse)
			{
				if (verificationCycle !== this.phoneVerificationCycle)
				{
					return;
				}

				this.isPhoneCodeManualOnly = true;
				this.phoneCodeError = this.getVerificationCodeErrorMessage(errorResponse);
				await this.$nextTick();
				this.$refs.phoneConfirmationForm?.focus();
			}
			finally
			{
				if (verificationCycle === this.phoneVerificationCycle)
				{
					this.isProcessingPhoneCode = false;
				}
			}
		},

		getVerificationCodeErrorMessage(errorResponse): string
		{
			return errorResponse?.errors?.[0]?.message
				|| Loc.getMessage('EXT_B24NET_LOGGING_IN_ERROR')
			;
		},

		resetEmailVerificationCycle(): void
		{
			this.emailVerificationCycle += 1;
			this.emailCode = '';
			this.emailCodeError = '';
			this.emailCodeIsOk = false;
			this.isProcessingEmailCode = false;
			this.isEmailCodeManualOnly = false;
		},

		resetPhoneVerificationCycle(): void
		{
			this.phoneVerificationCycle += 1;
			this.phoneCode = '';
			this.phoneCodeError = '';
			this.phoneCodeIsOk = false;
			this.isProcessingPhoneCode = false;
			this.isPhoneCodeManualOnly = false;
		},

		resetVerificationCycles(): void
		{
			this.resetEmailVerificationCycle();
			this.resetPhoneVerificationCycle();
		},

		onPasswordChange(newPassword): void
		{
			this.password = newPassword;
		},

		onPasswordReady(isReady): void
		{
			this.isPasswordOk = isReady;
		},

		async onPasswordEditContinueClick(): void
		{
			if (!this.password || !this.isPasswordOk)
			{
				return;
			}

			this.isProcessingPasswordEnter = true;

			try
			{
				await Ajax.changePassword(this.login, this.password, this.userCheckWord);
				if (this.redirectUrlWhenAuthorized)
				{
					UserRedirect.redirectToUrl(this.redirectUrlWhenAuthorized);

					return;
				}

				UserRedirect.redirectToPortalsAfterPasswordSetUp();
			}
			catch (errorResponse)
			{
				this.updatePasswordErrorIfAny(errorResponse);
			}
			finally
			{
				this.isProcessingPasswordEnter = false;
			}
		},

		onCaptchaResultChange(newResult): void
		{
			this.captchaError = '';
			this.captchaResult = newResult;
		},

		onResendTimeoutEnd(): void
		{
			this.resendTimeout = 0;
		},

		async onResendEmailClick(): void
		{
			const userSign = this.passwordRestoreResult?.USER_SIGN;
			this.resetEmailVerificationCycle();

			if (this.isPrefetchCaptchaFailure || !userSign)
			{
				try
				{
					await this.tryRestorePasswordForAccount();
					this.isPrefetchCaptchaFailure = false;
					Analytics.emitSendEmailRestorePasswordEvent(this.userAccount.id ?? null);
				}
				catch (errorResponse)
				{
					this.updateCaptchaErrorIfAny(errorResponse);
					resetCaptcha(this.captchaSettings.captchaType);
				}

				return;
			}

			try
			{
				const response = await Ajax.reSendPasswordRestoreEmailCode(
					userSign,
					this.captchaResult,
				);
				this.passwordRestoreResult = response.data ?? this.passwordRestoreResult;
				this.resendTimeout = response.data?.TIMEOUT ?? response.data?.DATE_SEND ?? 0;
				this.captchaResult = null;
				Analytics.emitSendEmailRestorePasswordEvent(this.userAccount.id ?? null);
			}
			catch (errorResponse)
			{
				this.updateCaptchaErrorIfAny(errorResponse);
			}

			resetCaptcha(this.captchaSettings.captchaType);
		},

		async onResendSmsClick(): void
		{
			const dataSign = this.passwordRestoreResult?.DATA_SIGN;
			this.resetPhoneVerificationCycle();

			if (this.isPrefetchCaptchaFailure || !dataSign)
			{
				try
				{
					Analytics.emitSmsResendClick();
					await this.tryRestorePasswordForAccount();
					this.isPrefetchCaptchaFailure = false;
				}
				catch (errorResponse)
				{
					this.updateCaptchaErrorIfAny(errorResponse);
					resetCaptcha(this.captchaSettings.captchaType);
				}

				return;
			}

			try
			{
				Analytics.emitSmsResendClick();
				const response = await Ajax.reSendPasswordRestorePhoneCode(
					dataSign,
					this.captchaResult,
				);
				this.captchaResult = null;
				this.resendTimeout = response.data?.DATE_SEND ?? 0;
				this.smsResendDataSignature = response.data?.DATA_SIGN ?? '';
			}
			catch (errorResponse)
			{
				this.updateCaptchaErrorIfAny(errorResponse);
			}

			resetCaptcha(this.captchaSettings.captchaType);
		},

		onCaptchaTypeChange(captchaType): void
		{
			CaptchaAnalytics.emitCaptchaShowEvent(captchaType, CaptchaScenarios.PASSWORD_RESTORE);
		},
	},

	template: `
		<div
			v-if="isPasswordRestoreBodyVisible"
			class="b24net-password-restore b24net-island"
			data-testid="b24network-password-restore-root"
		>
			<h2 class="b24net-password-restore__title" v-html="headingText"></h2>

			<PasswordRestoreForm
				v-if="isPasswordRestoreLoginStep"
				class="b24net-password-restore__login"
				data-testid="b24network-password-restore-login-form"
				:login-value="login"
				:login-error="loginError"
				:captcha-error="captchaError"
				:captcha-settings="captchaSettings"
				:login-title-text="passwordRestoreLabel"
				:show-captcha="captchaSettings.isEnabled"
				@login-change="onLoginChange"
				@captcha-type-change="onCaptchaTypeChange"
				@captcha-result-change="onCaptchaResultChange"
				@continue-click="onPassRestoreFormContinueClick"
			/>

			<PhoneAndEmailConfirmationForm
				v-if="isEmailConfirmationCodeStep"
				ref="emailConfirmationForm"
				:key="'email-' + (passwordRestoreResult?.USER_SIGN ?? '') + '-' + resendTimeout"
				class="b24net-password-restore__email-code"
				:user-contact-text="login"
				:code-error="emailCodeError"
				:is-phone-confirmation="false"
				:captcha-error="captchaError"
				:confirmation-code="emailCode"
				:is-link-disabled="isResendLinkDisabled"
				:resend-timeout-sec="resendTimeout"
				:resetTimeoutOnResendClick="false"
				:captcha-settings="captchaSettings"
				:show-captcha="showEmailCodeCaptcha"
				:is-waiting-for-continue="isProcessingEmailCode"
				:show-continue-button="showEmailCodeContinueButton"
				:is-continue-disabled="isEmailCodeContinueDisabled"
				@code-change="onEmailCodeChange"
				@code-ready="onEmailCodeReady"
				@resend-click="onResendEmailClick"
				@continue-click="onEmailCodeContinueClick"
				@resend-timeout-ended="onResendTimeoutEnd"
				@captcha-result-change="onCaptchaResultChange"
			/>

			<PhoneAndEmailConfirmationForm
				v-if="isPhoneConfirmationCodeStep"
				ref="phoneConfirmationForm"
				:key="'phone-' + (passwordRestoreResult?.DATA_SIGN ?? '') + '-' + resendTimeout"
				class="b24net-password-restore__phone-code"
				:user-contact-text="login"
				:code-error="phoneCodeError"
				:is-phone-confirmation="true"
				:captcha-error="captchaError"
				:confirmation-code="phoneCode"
				:is-link-disabled="isResendLinkDisabled"
				:resend-timeout-sec="resendTimeout"
				:resetTimeoutOnResendClick="false"
				:captcha-settings="captchaSettings"
				:show-captcha="showPhoneCodeCaptcha"
				:is-waiting-for-continue="isProcessingPhoneCode"
				:show-continue-button="showPhoneCodeContinueButton"
				:is-continue-disabled="isPhoneCodeContinueDisabled"
				@code-change="onPhoneCodeChange"
				@code-ready="onPhoneCodeReady"
				@resend-click="onResendSmsClick"
				@continue-click="onPhoneCodeContinueClick"
				@resend-timeout-ended="onResendTimeoutEnd"
				@captcha-result-change="onCaptchaResultChange"
			/>

			<PasswordEditForm
				v-if="isPasswordEditStep"
				data-testid="b24network-password-restore-password-form"
				:password-value="password"
				:user-account="userAccount"
				:show-password-repeat="false"
				:password-error="passwordError"
				:is-waiting="isProcessingPasswordEnter"
				:is-continue-disabled="isPasswordOk === false"
				@password-ready="onPasswordReady"
				@password-change="onPasswordChange"
				@continue-click="onPasswordEditContinueClick"
			/>

			<LegalLinks
				class="b24net-page-base__legal-links"
				:links="legalLinks"
				:type-of-captcha-on-page="typeOfCaptchaOnPage"
			/>
		</div>
	`,
};
